/**
 * Vista de piso en vivo (top-down animada).
 *
 * Una cabina por etapa del embudo y, dentro de cada una, una fila en serpentina
 * de figuras estilo Mii que avanza hacia la ventanilla. Es la única pieza del
 * sistema donde el volumen se percibe ANTES de leer la cifra: ver la fila crecer
 * comunica más rápido que el número que la encabeza.
 *
 * Vive aquí, fuera de cualquier página, porque la usan tanto el monitor del
 * portal (/admin/monitor) como el tablero de televisor (/tv). Tenerla duplicada
 * significaba que cualquier ajuste de la animación había que hacerlo dos veces.
 *
 * TEMA. Los colores salen de alias propios que caen primero en los tokens del
 * televisor (--tv-*) y, si no existen —es decir, dentro del portal—, en los del
 * portal. Así la misma animación se ve correcta sobre papel y sobre fondo
 * oscuro sin una sola regla duplicada.
 */
import {
  Fragment, useEffect, useRef, useState,
  type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './PisoTopDown.module.css';

export interface EtapaPiso {
  clave: string;
  etiqueta: string;
  valor: number;
  color: string;
  icono?: ReactNode;
  /** Muestra el aviso de cuello de botella sobre el piso de esta cabina. */
  cuelloBotella?: boolean;
}

// Paletas para las figuras — colores ALEATORIOS por paciente: piel de varias
// etnias, pelo común + algunos no tan comunes, camisas frescas (no muy fuertes),
// y accesorios para algunas mujeres (pelo largo, broche, aretes).
const SKINS = ['#f7d7be', '#f0c8a0', '#e8b98c', '#d9a066', '#c68642', '#a86b3c', '#8d5524', '#5e3a1e'];
const HAIR_COMMON = ['#141414', '#2a1a0f', '#43301c', '#6b4423', '#946a3f', '#c79a4b', '#dcc784', '#8c8c8c', '#bdbdbd'];
const HAIR_RARE = ['#a55050', '#d2805f', '#7e5e8e', '#c98bb0', '#6f97af'];
const SHIRTS = ['#a8cee5', '#a6dccd', '#b1ddb8', '#f7ccbc', '#f1c4d5', '#cfc3ed', '#f6e0ad', '#c2cdd7', '#ebc6a3', '#c2e1d8'];
const BOWS = ['#e9799f', '#f0a93f', '#d65a8f', '#7ab8e0', '#b98fd6', '#ef6f6f'];

const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

type Pal = { skin: string; hair: string; shirt: string; female: boolean; bow: string | null; earrings: boolean };

function randomPal(): Pal {
  const female = Math.random() < 0.45;
  return {
    skin: pick(SKINS),
    hair: Math.random() < 0.85 ? pick(HAIR_COMMON) : pick(HAIR_RARE),
    shirt: pick(SHIRTS),
    female,
    bow: female && Math.random() < 0.55 ? pick(BOWS) : null,
    earrings: female && Math.random() < 0.45,
  };
}

type Persona = { id: number; pal: Pal };

const REACCIONES = ['jump', 'zoom', 'wave', 'excl', 'quest', 'dots', 'chat', 'chat', 'chat', 'chat'];
const BURBUJA: Record<string, string> = { excl: '!', quest: '?', dots: '···' };

// Frases típicas de gente haciendo fila (máx 2–3 palabras) → burbujas tipo chat.
const FRASES = [
  '¿Cuánto falta?', '¿Qué hora es?', 'Mira atrás', 'Ya casi', '¿Falta mucho?',
  'No avanza', 'Qué fila', 'Por fin', '¿Ya casi?', 'Tengo prisa',
  '¿En serio?', 'Qué calor', '¿Sigo aquí?', 'Apúrense', 'Uf, qué sueño',
];

// Eventos que dispara el coordinador global: las reacciones + "salir de la fila".
const EVENTOS = [...REACCIONES, 'stray'];

const sinMovimiento = () =>
  typeof window !== 'undefined' && !!window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Cola en zigzag (serpentina): 3 por fila alternando dirección, con guía
 * punteada que traza el recorrido y fluye hacia el frente. El frente (i=0) queda
 * ABAJO, junto a la ventanilla. Muestra hasta 12 figuras; si hay menos, solo las
 * que hay.
 */
function ColaSerpiente({ count, evNonce, evType, evText }: {
  count: number; evNonce: number; evType: string; evText: string;
}) {
  const N = Math.min(count, 12);
  const idc = useRef(0);
  const [people, setPeople] = useState<Persona[]>(() =>
    Array.from({ length: Math.min(count, 12) }, () => ({ id: idc.current++, pal: randomPal() })),
  );
  const [react, setReact] = useState<{ id: number; type: string; text: string } | null>(null);
  const [stray, setStray] = useState<number | null>(null);
  // Arrastre estilo Wii: el mii agarrado se "despega" en un clon position:fixed
  // (portal a document.body, sin recortes) que sigue al cursor; al soltar lejos
  // CAMINA de regreso. lx/ly = esquina sup-izq del clon en coords de pantalla.
  const [drag, setDrag] = useState<{ id: number; lx: number; ly: number; returning: boolean; dur: number } | null>(null);
  const dragRef = useRef<{ id: number; ox: number; oy: number; homeX: number; homeY: number; returning: boolean } | null>(null);
  const peopleRef = useRef(people);
  peopleRef.current = people;

  const reduce = sinMovimiento();

  // Sincroniza la longitud con el conteo (sin remontar los existentes).
  useEffect(() => {
    setPeople((prev) => {
      if (prev.length === N) return prev;
      if (prev.length < N) {
        const add = Array.from({ length: N - prev.length }, () => ({ id: idc.current++, pal: randomPal() }));
        return [...prev, ...add];
      }
      return prev.slice(0, N);
    });
  }, [N]);

  // AVANCE lento y DESINCRONIZADO entre cabinas (setTimeout recursivo aleatorio).
  useEffect(() => {
    if (N <= 0 || reduce) return;
    let t: ReturnType<typeof setTimeout>;
    const step = () => {
      // No avanzar la fila mientras se arrastra un mii (evitar que se desmonte).
      if (!dragRef.current) {
        setPeople((prev) => (prev.length ? [...prev.slice(1), { id: idc.current++, pal: randomPal() }] : prev));
      }
      t = setTimeout(step, 16000 + Math.random() * 12000); // 16–28s
    };
    t = setTimeout(step, 4000 + Math.random() * 12000); // arranque aleatorio
    return () => clearTimeout(t);
  }, [N, reduce]);

  // EVENTO individual (reacción O salir de la fila): se dispara SOLO cuando el
  // coordinador GLOBAL elige esta cabina (evNonce cambia). El tipo lo decide el
  // coordinador (evType) → así se ven TODOS los tipos, no solo el de salir.
  useEffect(() => {
    if (!evNonce || reduce) return;
    const ppl = peopleRef.current;
    if (!ppl.length) return;
    if (evType === 'stray') {
      if (ppl.length < 2) return;
      const idx = 1 + Math.floor(Math.random() * (ppl.length - 1));
      setStray(ppl[idx].id);
      const t = setTimeout(() => setStray(null), 2400);
      return () => clearTimeout(t);
    }
    const person = ppl[Math.floor(Math.random() * ppl.length)];
    setReact({ id: person.id, type: evType, text: evText });
    const t = setTimeout(() => setReact(null), evType === 'chat' ? 2800 : 1700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evNonce]);

  const onMiiDown = (e: ReactPointerEvent<HTMLSpanElement>, id: number) => {
    if (reduce || dragRef.current) return;
    // El span original conserva el pointer-capture (aunque quede oculto) → sigue
    // recibiendo move/up.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    const r = e.currentTarget.getBoundingClientRect();
    dragRef.current = { id, ox: e.clientX - r.left, oy: e.clientY - r.top, homeX: r.left, homeY: r.top, returning: false };
    setDrag({ id, lx: r.left, ly: r.top, returning: false, dur: 0 });
  };
  const onMiiMove = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const dr = dragRef.current;
    if (!dr || dr.returning) return;
    setDrag({ id: dr.id, lx: e.clientX - dr.ox, ly: e.clientY - dr.oy, returning: false, dur: 0 });
  };
  const onMiiUp = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const dr = dragRef.current;
    if (!dr || dr.returning) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    const lx = e.clientX - dr.ox, ly = e.clientY - dr.oy;
    const dist = Math.hypot(lx - dr.homeX, ly - dr.homeY);
    if (dist < 26) { dragRef.current = null; setDrag(null); return; } // cerca → vuelve directo
    const dur = Math.min(1.8, Math.max(0.5, dist / 230)); // velocidad ~230px/s
    dr.returning = true; // mantiene pausado el avance de la fila durante la caminata
    setDrag({ id: dr.id, lx, ly, returning: true, dur }); // fase 1: posición actual + modo caminar
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setDrag((d) => (d && d.id === dr.id && d.returning ? { ...d, lx: dr.homeX, ly: dr.homeY } : d)); // fase 2: camina a casa
    }));
    window.setTimeout(() => {
      setDrag((d) => (d && d.id === dr.id && d.returning ? null : d));
      if (dragRef.current && dragRef.current.id === dr.id) dragRef.current = null;
    }, dur * 1000 + 90);
  };

  if (N <= 0) return null;

  const COLS = [20, 62, 104]; // centros X en la caja de 124px
  const ROW_GAP = 40;
  const Y_BASE = 26; // centro de la fila del frente, medido desde abajo
  const maxRow = Math.floor((N - 1) / 3);
  const H = Y_BASE + maxRow * ROW_GAP + 30;
  const pt = (i: number) => {
    const row = Math.floor(i / 3);
    const pos = i % 3;
    const col = row % 2 === 0 ? pos : 2 - pos; // serpentea (alterna dirección)
    return { x: COLS[col], y: H - (Y_BASE + row * ROW_GAP) };
  };
  const d = Array.from({ length: N }, (_, i) => { const p = pt(i); return `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`; }).join(' ');

  return (
    <div className={styles.queueBoxTD} style={{ height: H }}>
      <svg className={styles.guideTD} viewBox={`0 0 124 ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <path d={d} className={styles.guidePathTD} />
      </svg>
      {people.map((person, i) => {
        const p = pt(i);
        const front = i === 0;
        const pal = person.pal;
        const floating = drag?.id === person.id;
        const rType = !floating && react && react.id === person.id ? react.type : null;
        const chatTxt = rType === 'chat' ? (react?.text ?? '') : '';
        const bubble = chatTxt || (rType ? BURBUJA[rType] : undefined);
        const reactCls = rType && !bubble ? styles[`react_${rType}`] : '';
        let left = p.x - 15;
        let top = p.y - 30;
        if (stray === person.id) { left += p.x < 62 ? 28 : -28; top += 10; }
        const palVars = { ['--skin']: pal.skin, ['--hair']: pal.hair, ['--shirt']: pal.shirt, ['--bow']: pal.bow ?? undefined };
        const inner = (
          <>
            <span className={styles.shadowTD} />
            {front && <span className={styles.ringTD} />}
            {bubble && <span className={`${styles.bubbleTD} ${chatTxt ? styles.bubbleChatTD : ''}`}>{bubble}</span>}
            <span className={`${styles.miiTD} ${front ? styles.frontTD : ''}`} style={{ animationDelay: `${((person.id % 13) * 0.12).toFixed(2)}s` }}>
              {pal.female && <span className={styles.miiHairLong} />}
              <span className={styles.miiNeck} />
              <span className={styles.miiFace}>
                <span className={styles.miiHead} />
                <span className={styles.miiHair} />
                <span className={`${styles.miiEye} ${styles.miiEyeL}`} />
                <span className={`${styles.miiEye} ${styles.miiEyeR}`} />
              </span>
              {pal.earrings && <><span className={`${styles.miiEarring} ${styles.miiEarringL}`} /><span className={`${styles.miiEarring} ${styles.miiEarringR}`} /></>}
              {pal.bow && <span className={styles.miiBow} />}
              <span className={styles.miiBody} />
              <span className={`${styles.miiArm} ${styles.miiArmL}`} />
              <span className={`${styles.miiArm} ${styles.miiArmR}`} />
            </span>
          </>
        );
        return (
          <Fragment key={person.id}>
            <span
              className={`${styles.figAbsTD} ${reactCls}`}
              onPointerDown={(e) => onMiiDown(e, person.id)}
              onPointerMove={onMiiMove}
              onPointerUp={onMiiUp}
              onPointerCancel={onMiiUp}
              style={{ left, top, visibility: floating ? 'hidden' : undefined, ...palVars } as CSSProperties}
            >
              {inner}
            </span>
            {floating && createPortal(
              <span
                className={`${styles.figAbsTD} ${styles.floatCloneTD} ${drag!.returning ? styles.walkingTD : styles.draggingTD}`}
                style={{
                  position: 'fixed', left: drag!.lx, top: drag!.ly, margin: 0, zIndex: 9999,
                  transform: drag!.returning ? 'scale(1)' : 'scale(1.14)',
                  transition: drag!.returning ? `left ${drag!.dur}s ease-in-out, top ${drag!.dur}s ease-in-out` : 'none',
                  ...palVars,
                } as CSSProperties}
              >
                {inner}
              </span>,
              document.body,
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * Piso completo: una cabina por etapa.
 *
 * El coordinador de eventos es GLOBAL a propósito: cada ~5–9 s elige UN evento
 * en UNA cabina con gente. Si cada cola disparara los suyos, la frecuencia total
 * se multiplicaría por el número de cabinas y el piso entero parecería una
 * verbena.
 */
export function PisoTopDown({ etapas, className = '' }: { etapas: EtapaPiso[]; className?: string }) {
  // Delta ▲/▼ por etapa respecto al refresco anterior.
  const [deltas, setDeltas] = useState<Record<string, number>>({});
  const prevRef = useRef<Record<string, number> | null>(null);
  const valores = etapas.map((e) => e.valor).join(',');

  useEffect(() => {
    const actual: Record<string, number> = {};
    for (const e of etapas) actual[e.clave] = e.valor;
    const previo = prevRef.current;
    if (previo) {
      const d: Record<string, number> = {};
      for (const k of Object.keys(actual)) d[k] = actual[k] - (previo[k] ?? 0);
      setDeltas(d);
    }
    prevRef.current = actual;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valores]);

  const cabinasRef = useRef<number[]>([]);
  cabinasRef.current = etapas.map((e) => e.valor);
  const [ev, setEv] = useState<{ cabina: number; tipo: string; texto: string; nonce: number }>(
    { cabina: -1, tipo: '', texto: '', nonce: 0 },
  );

  useEffect(() => {
    if (sinMovimiento()) return;
    let t: ReturnType<typeof setTimeout>;
    const tick = () => {
      const activos = cabinasRef.current.map((v, i) => (v > 0 ? i : -1)).filter((i) => i >= 0);
      if (activos.length) {
        const cabina = activos[Math.floor(Math.random() * activos.length)];
        const tipo = EVENTOS[Math.floor(Math.random() * EVENTOS.length)];
        const texto = tipo === 'chat' ? pick(FRASES) : '';
        setEv((prev) => ({ cabina, tipo, texto, nonce: prev.nonce + 1 }));
      }
      t = setTimeout(tick, 5000 + Math.random() * 4000); // 5–9 s GLOBAL
    };
    t = setTimeout(tick, 3000 + Math.random() * 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`${styles.floorTD} ${className}`}>
      {etapas.map((et, i) => {
        const delta = deltas[et.clave];
        return (
          <div key={et.clave} className={styles.cabinTD} style={{ ['--c']: et.color } as CSSProperties}>
            <div className={styles.cabinHeadTD}>
              <div className={styles.headRowTD}>
                {et.icono ? <span className={styles.iconPillTD}>{et.icono}</span> : <span />}
                {delta === undefined || delta === 0 ? (
                  <span className={styles.deltaZeroTD} aria-hidden="true">·</span>
                ) : (
                  <span className={`${styles.deltaTD} ${delta > 0 ? styles.deltaUpTD : styles.deltaDownTD}`}>
                    {delta > 0 ? '▲' : '▼'} {Math.abs(delta)}
                  </span>
                )}
              </div>
              <div className={styles.cabinCountTD}>{et.valor}</div>
              <div className={styles.cabinLabelTD}>{et.etiqueta}</div>
            </div>
            <div className={styles.cabinFloorTD}>
              {et.cuelloBotella && <span className={styles.bottleneckTD}>cuello de botella</span>}
              <div className={styles.ventanillaTD} />
              <ColaSerpiente
                count={et.valor}
                evNonce={ev.cabina === i ? ev.nonce : 0}
                evType={ev.tipo}
                evText={ev.texto}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
